import { useCallback, useEffect, useRef, useState } from 'react';
import { readPosSortPreference, savePosSortPreference } from '../utils/posSortPreferenceUtil';
import {
  POS_PRODUCT_SORTS,
  POS_PRODUCT_SORT_DEFAULT,
  POS_SORT_ASC,
  POS_SORT_DESC,
} from '../constants/posConst';

/**
 * The sort field and direction for one product screen, remembered per client.
 *
 * Opens on the sort this client last picked on this screen (falling back to
 * Name), and saves every change so the next sign-in opens the same way. The
 * saved value is read once on mount; see readPosSortPreference for why it is
 * not a live listener.
 *
 * Changing the FIELD resets the direction to that field's own
 * `defaultDirection` — "descending" means something different on each field,
 * so carrying it over would e.g. silently show the fullest shelves when
 * someone picking "Stocks available" almost always wants the emptiest.
 *
 * @param {object} params
 * @param {string} params.clientId
 * @param {string} params.screen - a POS_SORT_PREFERENCE_SCREENS value
 * @param {string} [params.actor] - the staff address to record on the audit trail
 * @returns {{sortKey: string, sortDir: string, loaded: boolean, changeSort: (key: string) => void, toggleDirection: () => void}}
 *   `loaded` is false until the saved sort has been read, so the screen can
 *   wait rather than flash Name order first
 */
function useSavedSort({ clientId, screen, actor = '' }) {
  const [sortKey, setSortKey] = useState(POS_PRODUCT_SORT_DEFAULT);
  const [sortDir, setSortDir] = useState(POS_SORT_ASC);
  const [loaded, setLoaded] = useState(false);
  // Set once the user picks a sort, so a slow read arriving afterwards does
  // not undo their choice.
  const touched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    touched.current = false;
    setLoaded(false);
    readPosSortPreference(clientId, screen).then((saved) => {
      if (cancelled) return;
      if (saved && !touched.current) {
        setSortKey(saved.key);
        setSortDir(saved.direction);
      }
      setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [clientId, screen]);

  /**
   * Saves the sort as this client's default. Fire-and-forget: a failed save
   * only means the next sign-in opens on the old sort, which must not
   * interrupt the person using the till now.
   *
   * @param {string} key
   * @param {string} direction
   * @returns {void}
   */
  const persist = useCallback((key, direction) => {
    touched.current = true;
    savePosSortPreference(clientId, screen, { key, direction }, actor)
      .catch((error) => console.error('POS sort preference save failed:', error));
  }, [clientId, screen, actor]);

  /**
   * Switches to another sort field, with that field's default direction.
   * @param {string} key - a POS_PRODUCT_SORTS key
   * @returns {void}
   */
  const changeSort = useCallback((key) => {
    const direction = POS_PRODUCT_SORTS.find((sort) => sort.key === key)?.defaultDirection || POS_SORT_ASC;
    setSortKey(key);
    setSortDir(direction);
    persist(key, direction);
  }, [persist]);

  /**
   * Flips the direction of the current field.
   * @returns {void}
   */
  const toggleDirection = useCallback(() => {
    const direction = sortDir === POS_SORT_ASC ? POS_SORT_DESC : POS_SORT_ASC;
    setSortDir(direction);
    persist(sortKey, direction);
  }, [sortKey, sortDir, persist]);

  return { sortKey, sortDir, loaded, changeSort, toggleDirection };
}

export default useSavedSort;
